import type { Tool } from '../../types/tools'

export const RESEARCH_TOOLS: Tool[] = [
  {
    slug: 'market-research',
    name: 'Market Research',
    icon: 'globe',
    categories: ['find', 'evaluate'],
    featured: false,
    cardDescription:
      'Research a startup’s market, test sizing and growth assumptions, and organize the evidence.',
    seo: {
      title: 'AI Market Research for Investors & TAM SAM SOM Analysis | DiligenceGPT™',
      description:
        'Research startup markets for venture capital diligence. DiligenceGPT™ helps investors examine TAM, SAM and SOM assumptions, growth drivers and evidence for investment analysis.',
      keywords: ['AI market research for investors', 'startup market analysis', 'TAM SAM SOM analysis', 'venture capital market research'],
    },
    hero: {
      supporting:
        'Research market opportunities, investigate growth assumptions, and organize evidence for your investment analysis.',
      bullets: [
        'Pressure-test a startup’s market sizing',
        'Separate stated assumptions from evidence',
        'Organize findings for your memo',
      ],
    },
    whatIs: {
      question: 'What is AI market research for investors?',
      paragraphs: [
        'AI market research for investors helps a deal team understand the market a startup operates in — who the customers are, how large the addressable opportunity may be, what is driving growth, and which assumptions a company’s plan relies on.',
        'DiligenceGPT™’s Market Research tool is for analysts and associates evaluating whether a company’s market can support venture-scale outcomes. You provide the company, its stated market claims (often from the pitch deck) and the questions your team wants answered. The output organizes market definitions, a structured look at TAM, SAM and SOM reasoning, growth drivers and headwinds, and the evidence and assumptions behind each point.',
        'Market sizing always involves judgment. The tool makes assumptions explicit and highlights where evidence is weak, so your team can decide how much weight to give the numbers.',
      ],
    },
    steps: [
      {
        title: 'Define the company and questions',
        body: 'Provide the company, its market claims and the questions you need answered — sizing, segments, growth drivers or timing.',
      },
      {
        title: 'Run the research',
        body: 'DiligenceGPT™ researches the market, structures sizing logic and organizes evidence alongside the company’s own claims.',
      },
      {
        title: 'Review and refine',
        body: 'Challenge the assumptions, adjust the market definition and carry the findings into your memo.',
      },
    ],
    outputs: [
      { title: 'Market definition and segments', body: 'A clear statement of the market and customer segments being analyzed.' },
      { title: 'TAM, SAM and SOM reasoning', body: 'Sizing logic laid out step by step, with each assumption visible.' },
      { title: 'Growth drivers and headwinds', body: 'Factors that may accelerate or slow the market, with supporting evidence.' },
      { title: 'Company claims vs. evidence', body: 'Where the startup’s stated market view is supported, unsupported or unclear.' },
      { title: 'Open questions for founders', body: 'Points to clarify about go-to-market and target segments.' },
    ],
    preview: {
      kind: 'Market analysis',
      title: 'Market sizing review',
      subject: 'Tallyfox · Series A · AP automation for mid-market finance teams',
      table: {
        caption: 'Sample sizing logic',
        columns: ['Layer', 'Definition', 'Basis'],
        rows: [
          ['TAM', 'AP software spend, all company sizes', 'Company claim · top-down'],
          ['SAM', 'Mid-market firms in North America', 'Segment filter · bottom-up'],
          ['SOM', 'Reachable with current sales motion', 'Assumption · to validate'],
        ],
      },
      assumptions: ['Mid-market adoption rate of AP automation', 'Average contract value holds as company moves upmarket'],
      gaps: ['Customer counts by segment', 'Win rates against incumbent tools'],
    },
    process: {
      heading: 'Research through the lens of your thesis',
      body: 'Frame research around the markets and themes your fund focuses on, so findings are comparable across companies in the same space. Then map the players competing for that market.',
      configurable: ['Thesis markets and themes', 'Preferred sizing approach', 'Questions your IC expects answered'],
      next: { slug: 'competitor-analysis', prompt: 'Sized the market? Map the competitive landscape next.' },
    },
    faqs: [
      { question: 'What does Market Research do?', answer: 'It helps investors understand a startup’s market by structuring market definitions, sizing logic, growth drivers and the evidence behind each point.' },
      { question: 'What information do I need?', answer: 'The company and its market claims — usually from the pitch deck — plus the specific questions your team wants answered.' },
      { question: 'Can it calculate TAM, SAM and SOM?', answer: 'It lays out TAM, SAM and SOM reasoning with each assumption visible. Sizes depend on the definitions and assumptions used, so treat them as an analytical framework to test, not a definitive figure.' },
      { question: 'How should analysts review the results?', answer: 'Check the evidence behind each claim, challenge assumptions that drive the sizing, and note where the research relies on the company’s own statements.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'Market research usually follows the first screen and feeds competitor analysis, valuation work and the market section of your investment memo.' },
    ],
    finalCta: {
      heading: 'Know whether the market can carry the company.',
      body: 'Make sizing assumptions explicit and organize the evidence your IC will ask about.',
    },
    related: ['competitor-analysis', 'pitch-deck-screener', 'investment-memo-writer', 'valuation-comps', 'deal-sourcing', 'financial-analysis', 'founder-research', 'data-room-review', 'portfolio-reporting'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 290,
  },
  {
    slug: 'competitor-analysis',
    name: 'Competitor Analysis',
    icon: 'swords',
    categories: ['evaluate'],
    featured: false,
    cardDescription:
      'Map a startup’s competitors and compare products and business models to test its differentiation.',
    seo: {
      title: 'AI Competitor Analysis & Competitive Landscape for Startups | DiligenceGPT™',
      description:
        'Map a startup’s competitive landscape. DiligenceGPT™ helps investors identify competitors, compare products and business models, and investigate differentiation during diligence.',
      keywords: ['AI competitor analysis', 'startup competitive landscape', 'competitive market mapping', 'competitor research for investors'],
    },
    hero: {
      supporting:
        'Identify competitors, compare business models and products, and investigate a startup’s differentiation.',
      bullets: [
        'Find competitors a deck leaves out',
        'Compare products and business models side by side',
        'Test how defensible the differentiation is',
      ],
    },
    whatIs: {
      question: 'What is AI competitor analysis for investors?',
      paragraphs: [
        'AI competitor analysis helps investors build a picture of the competitive landscape around a startup — direct competitors, adjacent players, incumbents and substitutes — and compare how each approaches the same customer problem.',
        'DiligenceGPT™’s Competitor Analysis is for deal teams evaluating differentiation and defensibility. You provide the company and any competitors you already know about, including those named in the pitch deck. The output is a structured competitive map, a side-by-side comparison of products and business models, and an assessment of where the company’s claimed differentiation is supported and where it needs further diligence.',
        'Public information about private competitors is often incomplete. The analysis notes where information is limited so your team can decide what to verify through customer and expert calls.',
      ],
    },
    steps: [
      {
        title: 'Provide the company and known competitors',
        body: 'Add the startup, the competitors it names, and any others your team is already tracking.',
      },
      {
        title: 'Run the analysis',
        body: 'DiligenceGPT™ identifies additional players and compares products, positioning and business models.',
      },
      {
        title: 'Review and refine',
        body: 'Validate the map, remove irrelevant players and decide which differentiation claims need customer validation.',
      },
    ],
    outputs: [
      { title: 'Competitive landscape map', body: 'Direct competitors, adjacent players, incumbents and substitutes, grouped clearly.' },
      { title: 'Product comparison', body: 'How each player addresses the core customer problem.' },
      { title: 'Business model comparison', body: 'Pricing approach, target customer and go-to-market, where information is available.' },
      { title: 'Differentiation assessment', body: 'Which claimed advantages appear supported and which need more evidence.' },
      { title: 'Information limitations', body: 'Where competitor data is sparse or based on self-reported claims.' },
    ],
    preview: {
      kind: 'Competitive comparison',
      title: 'Competitive landscape',
      subject: 'Tallyfox · Series A · AP automation for mid-market finance teams',
      table: {
        caption: 'Sample comparison — fictional companies',
        columns: ['Company', 'Segment', 'Model', 'Positioning'],
        rows: [
          ['Tallyfox', 'Mid-market', 'Per-seat SaaS', 'Workflow-first AP'],
          ['Ledgerwise', 'SMB', 'Usage-based', 'Bundled with payments'],
          ['Paystream ERP add-on', 'Enterprise', 'ERP module', 'Incumbent extension'],
          ['Manual + spreadsheets', 'All', '—', 'Substitute'],
        ],
      },
      gaps: ['Ledgerwise pricing not public', 'Customer switching data not available'],
    },
    process: {
      heading: 'Comparable across every deal in a sector',
      body: 'Use consistent comparison dimensions so competitive work on one company informs the next deal in the same space. Pair the landscape with funding benchmarks when it is time to talk price.',
      configurable: ['Comparison dimensions', 'Sectors and segments of focus', 'Known companies to always include'],
      next: { slug: 'valuation-comps', prompt: 'Mapped the competition? Benchmark valuation and comps next.' },
    },
    faqs: [
      { question: 'What does Competitor Analysis do?', answer: 'It identifies a startup’s competitors and substitutes, compares products and business models, and helps assess how well the company’s differentiation is supported.' },
      { question: 'What information do I need?', answer: 'The company you are evaluating and any competitors already known — for example, those listed in the pitch deck.' },
      { question: 'Can I set my own comparison criteria?', answer: 'Yes. Comparing on the dimensions your team cares about keeps analysis consistent across companies in the same sector.' },
      { question: 'How should analysts review the results?', answer: 'Confirm the competitive set with your own knowledge and customer calls, and treat differentiation findings as hypotheses to validate. Information on private competitors may be incomplete.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'It typically follows market research and informs the risk, positioning and valuation sections of your investment memo.' },
    ],
    finalCta: {
      heading: 'See the whole field before you back one player.',
      body: 'Map competitors and test differentiation with evidence, not slides.',
    },
    related: ['market-research', 'valuation-comps', 'investment-memo-writer', 'pitch-deck-screener', 'founder-research', 'deal-sourcing', 'financial-analysis', 'data-room-review', 'portfolio-reporting'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 270,
  },
  {
    slug: 'founder-research',
    name: 'Founder Research',
    icon: 'user-search',
    categories: ['evaluate'],
    featured: false,
    cardDescription:
      'Organize public professional background, assess relevant experience and prepare call questions.',
    seo: {
      title: 'Founder Research Tool for Startup Team Due Diligence | DiligenceGPT™',
      description:
        'Research startup founders using publicly available professional information. DiligenceGPT™ helps investors assess relevant experience and prepare questions for founder and reference calls.',
      keywords: ['founder research tool', 'AI founder background research', 'startup team due diligence', 'founder experience analysis'],
    },
    hero: {
      supporting:
        'Organize publicly available professional information, assess relevant experience, and prepare questions for founder and reference calls.',
      bullets: [
        'See each founder’s relevant experience in one place',
        'Connect team background to the company’s plan',
        'Prepare focused founder and reference questions',
      ],
    },
    whatIs: {
      question: 'What is a founder research tool?',
      paragraphs: [
        'A founder research tool helps investors understand the people behind a startup — their professional histories, the experience they bring to the problem, and the gaps a team may need to fill as the company grows.',
        'DiligenceGPT™’s Founder Research is for investors preparing for founder meetings and reference calls. You provide the founders’ names and the company, along with the information the founders have shared, such as the team slide. It organizes publicly available professional information, relates each founder’s experience to the company’s plan, and drafts questions for founder and reference conversations.',
        'Founder Research works only with publicly available and company-provided information. It does not access private records and is not a background check, identity verification or screening service. Any conclusions about people should be formed through direct conversations and references.',
      ],
    },
    steps: [
      {
        title: 'Provide the team and company',
        body: 'Add the founders’ names, the company, and the team information the company has shared.',
      },
      {
        title: 'Run the research',
        body: 'DiligenceGPT™ organizes public professional information and relates experience to the company’s plan.',
      },
      {
        title: 'Review and prepare',
        body: 'Confirm details with the founders, then use the questions to structure founder and reference calls.',
      },
    ],
    outputs: [
      { title: 'Professional background summaries', body: 'Roles, companies and areas of expertise from public and company-provided sources.' },
      { title: 'Founder–market experience', body: 'How each founder’s experience relates to the problem the company is solving.' },
      { title: 'Team coverage and gaps', body: 'Functions the current team covers well and roles that may need to be filled.' },
      { title: 'Founder call questions', body: 'Questions to understand motivation, decision-making and plans for building the team.' },
      { title: 'Reference call guide', body: 'Topics to explore with references, tied to what the team will need to execute.' },
    ],
    preview: {
      kind: 'Team report',
      title: 'Founding team overview',
      subject: 'Tallyfox · Series A · AP automation — fictional founders',
      rows: [
        { label: 'CEO · A. Rivera (fictional)', status: 'strong', detail: 'Led AP operations at a mid-market manufacturer; direct exposure to the customer problem.', source: 'Team slide · public profile' },
        { label: 'CTO · J. Okafor (fictional)', status: 'strong', detail: 'Built payments integrations at a fintech platform.', source: 'Public profile' },
        { label: 'Go-to-market leadership', status: 'gap', detail: 'No dedicated sales leader listed for planned upmarket move.', source: 'Team slide' },
      ],
      gaps: ['Plan for sales leadership hire', 'How co-founders divide decision rights'],
    },
    process: {
      heading: 'Team diligence that matches how you evaluate founders',
      body: 'Focus research on the founder attributes your fund weighs most, and keep notes consistent across deals. Then connect what you learn about the team to the company’s numbers.',
      configurable: ['Founder attributes your fund prioritizes', 'Call question templates', 'Reference call topics'],
      next: { slug: 'financial-analysis', prompt: 'Researched the team? Review the financials next.' },
    },
    faqs: [
      { question: 'What does Founder Research do?', answer: 'It organizes publicly available professional information about a startup’s founders, relates their experience to the company’s plan, and drafts questions for founder and reference calls.' },
      { question: 'Does it run background checks or access private records?', answer: 'No. It uses publicly available and company-provided information only. It is not a background check, identity verification or consumer reporting service.' },
      { question: 'What information do I need?', answer: 'The founders’ names, the company, and any team information the company has provided, such as a team slide.' },
      { question: 'How should analysts review the results?', answer: 'Confirm details directly with founders, recognize that public information can be incomplete or outdated, and form views about people through conversations and references.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'Founder research usually runs alongside early diligence and feeds the team section of your investment memo.' },
    ],
    finalCta: {
      heading: 'Walk into founder calls already prepared.',
      body: 'Understand relevant experience and know exactly what to ask.',
    },
    related: ['pitch-deck-screener', 'investment-memo-writer', 'financial-analysis', 'data-room-review', 'competitor-analysis', 'market-research', 'valuation-comps', 'deal-sourcing', 'portfolio-reporting'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 230,
  },
]
